import { useState } from 'react';

import { useCheckHandleQuery } from '@/shared/api/endpoints';
import { useDebounced } from '@/shared/lib/useDebounced';

/** How long typing has to pause before the server is asked. */
const PAUSE_MS = 300;

export type HandleStatus =
  /** The field was emptied: the server will make a handle from the name. */
  | 'empty'
  /** Made from the display name by the server, and free. */
  | 'suggested'
  | 'checking'
  | 'available'
  | 'taken'
  | 'reserved'
  | 'invalid';

export interface HandleState {
  /** What the field shows: what was typed, or the suggestion while nothing was. */
  value: string;
  status: HandleStatus;
  /** Whether the form can be sent as it is. */
  ok: boolean;
  /** Whether what was typed is known to be unusable (and not merely being checked). */
  refused: boolean;
  /** What to send: undefined leaves the choice to the server. */
  chosen: string | undefined;
  type: (value: string) => void;
}

/**
 * The handle of an account that is finishing its registration: what the addresses
 * of everything it publishes will start with.
 *
 * It starts as the one the server made from the display name. Once the person
 * types in the field it is theirs: what they type is checked as they type, and an
 * emptied field stays empty, to be typed into (the server then chooses, as it
 * would have). It cannot be changed afterwards, so the form shows what it will be
 * before anything is sent.
 */
export function useHandle(suggested: string): HandleState {
  const [typed, setTyped] = useState<string | null>(null);
  const own = useDebounced(typed ?? '', PAUSE_MS);
  const check = useCheckHandleQuery({ handle: own }, { skip: typed === null || own === '' });

  const type = (value: string) => setTyped(value.toLowerCase());
  const untouched = { ok: true, refused: false, chosen: undefined, type };

  // Not sent while untouched: if somebody took it a moment ago, the server makes
  // the next free one instead of refusing a name nobody typed.
  if (typed === null) return { value: suggested, status: 'suggested', ...untouched };
  if (typed === '') return { value: '', status: 'empty', ...untouched };

  const answer = own === typed && !check.isFetching ? check.data : undefined;
  const status: HandleStatus = !answer
    ? 'checking'
    : answer.available
      ? 'available'
      : (answer.reason ?? 'invalid');
  return {
    value: typed,
    status,
    ok: status === 'available',
    refused: status === 'taken' || status === 'reserved' || status === 'invalid',
    chosen: status === 'available' ? answer?.handle : undefined,
    type,
  };
}
